import * as R from 'ramda'
// React
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { HallType } from '@weblite/ts-action/hall'
// Logic
import {
  COURSE_VITRINE_TAG,
  ISSUE_TAG,
  useHall,
  useRoomIsEducational,
} from '$logic/hall/react-hook'
import { useRoom } from '$logic/room/react-hook'
import { changeHallInfo } from '$logic/hall'
import { useClientUserPermissions } from '$logic/subscription/react-hook'
import { closeHallSettings } from '$logic/router'
// Common
import { AppBarBackTitleConfirm } from '$common/app-bar/app-bar-back-title-confirm'
import { PageWithAppBar } from '$common/page-with-app-bar'
import { SettingsSection } from '$common/settings-section'
import { showSaveChangesLoadingModal } from '$comp/modals/loading-modal'
// Localization
import { areEqualStr } from '$func/text'
import { t } from '$i18n'
import { AppBarBackTitle } from '$common/app-bar/app-bar-back-title'
// Component
import { HallSettingsActions } from './hall-settings-actions'
import {
  HallSettingsProfile,
  HallSettingsProfileApiType,
} from './hall-settings-profile'
import { HallSettingsCriticalActions } from './hall-settings-critical-actions'
import { HallSettingsOptions } from './hall-settings-options'
import { addTagToHallTags, removeTagFromHallTags } from '$logic/hall/helper'
import { HallAdvancedSettingsOptions } from '$common/hall-advanced-settings-options'
import { useWorkspaceColorByRoomId } from '$logic/workspace'

interface Props {
  roomId: string
}

export function HallSettings({ roomId }: Props) {
  const { entity: hall, loading: hallLoading } = useHall(roomId)
  const profileRef = useRef<HallSettingsProfileApiType>(null)
  const room = useRoom(roomId)
  const permissions = useClientUserPermissions(roomId)
  const [newTags, setNewTags] = useState<string[]>([])
  const { roomIsEducational: isEducational } = useRoomIsEducational(roomId)
  const tags = hall?.tags
  useEffect(() => {
    setNewTags(tags || [])
  }, [tags])

  const toggleTag = useCallback(
    (tag: string) => {
      if (newTags.includes(tag)) setNewTags(removeTagFromHallTags(tag, newTags))
      if (!newTags.includes(tag)) setNewTags(addTagToHallTags(tag, newTags))
    },
    [newTags],
  )

  const vitrineEnabled = useMemo(() => {
    return newTags.includes(COURSE_VITRINE_TAG)
  }, [newTags])

  const issueEnabled = useMemo(() => {
    return newTags.includes(ISSUE_TAG)
  }, [newTags])

  const prevProtectContent = !!hall?.protectContent

  const [protectContent, setProtectContent] = useState<boolean | null>(null)

  const backgroundColor = useWorkspaceColorByRoomId(roomId)
  // TODO: show skeleton if needed
  if (!hall) return null

  const isGroup = room?.type === 'GROUP'
  const displayType = isGroup ? t('common.group') : t('common.channel')

  const onConfirmHandler = async () => {
    const { current: profileApi } = profileRef
    if (!profileApi) return

    const name = profileApi.getName()
    const description = profileApi.getDescription()
    const image = profileApi.getImage()
    const patch = {
      ...(!areEqualStr(description, hall.description) && { description }),
      ...(!areEqualStr(name, hall.name) && { name }),
      ...(!areEqualStr(image, hall.image) && { image }),
      ...(!areEqualStr(tags?.toString() || '', newTags.toString()) && {
        tags: newTags,
      }),
      ...(protectContent !== prevProtectContent && {
        protectContent: !!protectContent,
      }),
    }

    if (!R.isEmpty(patch)) {
      showSaveChangesLoadingModal()
      await changeHallInfo(roomId, patch)
      await closeCurrentModal()
    }
    await closeHallSettings()
  }

  return (
    <PageWithAppBar
      useCustomScroller
      appBar={
        permissions?.changeHallInfo ? (
          <AppBarBackTitleConfirm
            title={`${t('common.settings')} ${displayType}`}
            onConfirm={onConfirmHandler}
            confirmDisabled={hallLoading}
            onBack={closeHallSettings}
          />
        ) : (
          <AppBarBackTitle
            title={`${t('common.settings')} ${displayType}`}
            onBack={closeHallSettings}
            backgroundColor={backgroundColor}
          />
        )
      }
    >
      <SettingsSection>
        <HallSettingsProfile
          hall={hall}
          ref={profileRef}
          editable={Boolean(permissions?.changeHallInfo)}
          isGroup={isGroup}
        />
      </SettingsSection>

      {permissions?.changeHallInfo && (
        <SettingsSection>
          <HallSettingsOptions
            hall={hall}
            isGroup={isGroup}
            isEducational={isEducational}
          />
        </SettingsSection>
      )}

      {permissions?.isAdmin && (
        <SettingsSection>
          <HallSettingsActions
            isGroup={isGroup}
            hallId={roomId}
            permissions={permissions}
          />
        </SettingsSection>
      )}

      {isEducational && (
        <SettingsSection
          headerColor="secondary700"
          headerText={t(`createHall.advancedOptions`)}
        >
          <HallAdvancedSettingsOptions
            vitrineEnabled={vitrineEnabled}
            issueEnabled={issueEnabled}
            protectContent={protectContent ?? prevProtectContent}
            setProtectContent={setProtectContent}
            toggleTag={toggleTag}
          />
        </SettingsSection>
      )}
      {permissions?.isOwner && (
        <SettingsSection>
          <HallSettingsCriticalActions
            hall={hall}
            hallType={room?.type as HallType}
          />
        </SettingsSection>
      )}
    </PageWithAppBar>
  )
}
